import React, { type CSSProperties } from "react";
import {
  ArrowRight,
  BookOpen,
  CheckCircle2,
  ClipboardCheck,
  ExternalLink,
  LockKeyhole,
  ShieldCheck,
  ShoppingCart,
  Sparkles,
  TriangleAlert,
  UserRound,
  Zap,
} from "lucide-react";
import {
  productFeatureTokens,
  type ProductFeatureColorToken,
} from "./ProductFeature.tokens";

export { productFeatureTokens } from "./ProductFeature.tokens";
export type {
  ProductFeatureColorToken,
  ProductFeatureIconSlot,
} from "./ProductFeature.tokens";

type ProductFeatureIconComponent = React.ComponentType<{
  className?: string;
  size?: number;
  style?: CSSProperties;
  strokeWidth?: number;
  "aria-hidden"?: boolean;
}>;

const iconRegistry = {
  arrowRight: ArrowRight,
  bookOpen: BookOpen,
  checkCircle: CheckCircle2,
  clipboardCheck: ClipboardCheck,
  externalLink: ExternalLink,
  lock: LockKeyhole,
  shieldCheck: ShieldCheck,
  shoppingCart: ShoppingCart,
  sparkles: Sparkles,
  alertTriangle: TriangleAlert,
  user: UserRound,
  zap: Zap,
} satisfies Record<string, ProductFeatureIconComponent>;

export type ProductFeatureIconToken = keyof typeof iconRegistry;

export type ProductFeatureLinkComponent = React.ComponentType<{
  href: string;
  children?: React.ReactNode;
  className?: string;
  title?: string;
  target?: string;
  rel?: string;
  style?: CSSProperties;
  "aria-label"?: string;
  [key: string]: any;
}>;

export type ProductFeatureImage = {
  src?: string;
  url?: string;
  original?: string;
  thumbnail?: string;
  alt?: string;
  title?: string;
  href?: string;
  ariaLabel?: string;
  objectPosition?: string;
  mobileObjectPosition?: string;
  loading?: "eager" | "lazy";
  openInNewTab?: boolean;
  target?: string;
  rel?: string;
};

export type ProductFeatureAction = {
  label: React.ReactNode;
  href: string;
  ariaLabel?: string;
  title?: string;
  icon?: React.ReactNode;
  iconToken?: ProductFeatureIconToken;
  hideIcon?: boolean;
  iconPosition?: "start" | "end";
  openInNewTab?: boolean;
  target?: string;
  rel?: string;
};

export type ProductFeaturePoint = {
  id?: string | number;
  label: React.ReactNode;
  description?: React.ReactNode;
  icon?: React.ReactNode;
  iconToken?: ProductFeatureIconToken;
  accentColor?: string;
};

export type ProductFeatureNote = {
  label: React.ReactNode;
  icon?: React.ReactNode;
  iconToken?: ProductFeatureIconToken;
};

export type ProductFeatureColors = Partial<Record<ProductFeatureColorToken, string>>;

export type ProductFeatureStyleSlot =
  | "root"
  | "shell"
  | "frame"
  | "layout"
  | "copy"
  | "eyebrow"
  | "title"
  | "subtitle"
  | "description"
  | "features"
  | "feature"
  | "actions"
  | "primaryAction"
  | "secondaryAction"
  | "footnote"
  | "media"
  | "productImage";

export type ProductFeatureStyles = Partial<
  Record<ProductFeatureStyleSlot, CSSProperties>
>;

export type ProductFeatureProps = {
  eyebrow?: React.ReactNode;
  title?: React.ReactNode;
  titleComponent?: "h1" | "h2" | "h3";
  subtitle?: React.ReactNode;
  description?: React.ReactNode;
  features?: ProductFeaturePoint[];
  primaryAction?: ProductFeatureAction;
  secondaryAction?: ProductFeatureAction;
  footnote?: React.ReactNode | ProductFeatureNote;

  backgroundImage?: ProductFeatureImage;
  backgroundImageSrc?: string;
  backgroundImageAlt?: string;
  backgroundObjectPosition?: string;
  backgroundMobileObjectPosition?: string;
  backgroundOpacity?: number;
  backgroundFilter?: string;

  productImage?: ProductFeatureImage;
  productImageSrc?: string;
  productImageAlt?: string;
  productObjectPosition?: string;
  imageHref?: string;
  imageAriaLabel?: string;
  imageOpenInNewTab?: boolean;
  imageTarget?: string;
  imageRel?: string;

  mediaPlacement?: "left" | "right";
  Link?: ProductFeatureLinkComponent;

  colors?: ProductFeatureColors;
  colours?: ProductFeatureColors;
  accentColor?: string;
  accentColour?: string;
  accentDarkColor?: string;
  accentDarkColour?: string;
  accentSoftColor?: string;
  accentSoftColour?: string;
  backgroundColor?: string;
  backgroundColour?: string;
  surfaceColor?: string;
  surfaceColour?: string;
  textColor?: string;
  textColour?: string;
  mutedTextColor?: string;
  mutedTextColour?: string;

  maxWidth?: number | string;
  minHeight?: number | string;
  mobileMinHeight?: number | string;
  mediaMinHeight?: number | string;
  productMaxWidth?: number | string;
  className?: string;
  style?: CSSProperties;
  styles?: ProductFeatureStyles;
  ariaLabel?: string;
};

type ProductFeatureStyle = CSSProperties &
  Record<`--product-feature-${string}`, string | number | undefined>;

const t = productFeatureTokens;

const DefaultLink: ProductFeatureLinkComponent = ({ href, children, ...props }) => (
  <a href={href} {...props}>
    {children}
  </a>
);

const productFeatureCss = `
.product-feature {
  --product-feature-bg: ${t.colors.background};
  --product-feature-surface: ${t.colors.surface};
  --product-feature-surface-soft: ${t.colors.surfaceSoft};
  --product-feature-text: ${t.colors.text};
  --product-feature-text-soft: ${t.colors.textSoft};
  --product-feature-text-muted: ${t.colors.textMuted};
  --product-feature-accent: ${t.colors.accent};
  --product-feature-accent-dark: ${t.colors.accentDark};
  --product-feature-accent-soft: ${t.colors.accentSoft};
  --product-feature-border: ${t.colors.border};
  --product-feature-divider: ${t.colors.divider};
  --product-feature-button-text: ${t.colors.buttonText};
  --product-feature-image-fallback: ${t.colors.imageFallback};
  --product-feature-focus: ${t.colors.focus};
  --product-feature-overlay-start: ${t.colors.overlayStart};
  --product-feature-overlay-middle: ${t.colors.overlayMiddle};
  --product-feature-overlay-end: ${t.colors.overlayEnd};
  --product-feature-shadow: ${t.colors.shadow};
  --product-feature-product-shadow: ${t.colors.productShadow};
  --product-feature-max-width: ${t.sizes.maxWidth}px;
  --product-feature-min-height: ${t.sizes.minHeight}px;
  --product-feature-mobile-min-height: ${t.sizes.mobileMinHeight}px;
  --product-feature-media-min-height: ${t.sizes.mediaMinHeight}px;
  --product-feature-product-max-width: ${t.sizes.productMaxWidth}px;
  --product-feature-bg-position: ${t.defaults.backgroundObjectPosition};
  --product-feature-bg-mobile-position: ${t.defaults.backgroundMobileObjectPosition};
  --product-feature-product-position: ${t.defaults.productObjectPosition};
  --product-feature-bg-opacity: 1;
  --product-feature-bg-filter: none;
  width: 100%;
  overflow: hidden;
  background: var(--product-feature-bg);
  color: var(--product-feature-text);
  font-family: ${t.typography.fontFamily};
  box-sizing: border-box;
  container-type: inline-size;
}
.product-feature,
.product-feature * {
  box-sizing: border-box;
}
.product-feature-shell {
  width: 100%;
  max-width: var(--product-feature-max-width);
  margin: 0 auto;
  padding: ${t.spacing.shellY}px ${t.spacing.shellX}px;
}
.product-feature-frame {
  position: relative;
  min-height: var(--product-feature-min-height);
  overflow: hidden;
  border: 1px solid var(--product-feature-border);
  border-radius: ${t.radius.frame}px;
  background: var(--product-feature-surface);
  box-shadow: var(--product-feature-shadow);
  isolation: isolate;
}
.product-feature-background,
.product-feature-background-image,
.product-feature-scrim {
  position: absolute;
  inset: 0;
}
.product-feature-background {
  z-index: 0;
  background: var(--product-feature-image-fallback);
}
.product-feature-background-image {
  display: block;
  width: 100%;
  height: 100%;
  object-fit: cover;
  object-position: var(--product-feature-bg-position);
  opacity: var(--product-feature-bg-opacity);
  filter: var(--product-feature-bg-filter);
}
.product-feature-scrim {
  z-index: 1;
  background:
    linear-gradient(90deg, var(--product-feature-overlay-start) 0%, var(--product-feature-overlay-middle) 43%, var(--product-feature-overlay-end) 100%),
    linear-gradient(180deg, rgba(0, 0, 0, 0.22) 0%, rgba(0, 0, 0, 0.66) 100%);
  pointer-events: none;
}
.product-feature-layout {
  position: relative;
  z-index: 2;
  display: grid;
  min-height: var(--product-feature-min-height);
  grid-template-columns: minmax(0, 0.92fr) minmax(300px, 0.9fr);
  align-items: center;
  gap: 42px;
  padding: ${t.spacing.framePaddingY}px ${t.spacing.framePaddingX}px;
}
.product-feature-frame[data-has-media="false"] .product-feature-layout {
  grid-template-columns: minmax(0, 760px);
}
.product-feature-frame[data-media-placement="left"] .product-feature-copy {
  order: 2;
}
.product-feature-frame[data-media-placement="left"] .product-feature-media {
  order: 1;
}
.product-feature-copy {
  display: grid;
  max-width: 660px;
  min-width: 0;
  gap: ${t.spacing.copyGap}px;
  align-content: center;
}
.product-feature-eyebrow {
  width: fit-content;
  max-width: 100%;
  margin: 0;
  padding-bottom: 9px;
  border-bottom: 1px solid var(--product-feature-accent);
  color: var(--product-feature-accent);
  font-size: ${t.typography.eyebrow.fontSize}px;
  line-height: ${t.typography.eyebrow.lineHeight};
  font-weight: ${t.typography.eyebrow.fontWeight};
  letter-spacing: ${t.typography.eyebrow.letterSpacing};
  text-transform: ${t.typography.eyebrow.textTransform};
  overflow-wrap: anywhere;
}
.product-feature-title {
  max-width: 100%;
  margin: 0;
  color: var(--product-feature-text);
  font-size: ${t.typography.title.fontSize}px;
  line-height: ${t.typography.title.lineHeight};
  font-weight: ${t.typography.title.fontWeight};
  letter-spacing: ${t.typography.title.letterSpacing};
  white-space: pre-line;
  overflow-wrap: anywhere;
  text-wrap: balance;
}
.product-feature-subtitle {
  max-width: 100%;
  margin: 0;
  color: var(--product-feature-text);
  font-size: ${t.typography.subtitle.fontSize}px;
  line-height: ${t.typography.subtitle.lineHeight};
  font-weight: ${t.typography.subtitle.fontWeight};
  letter-spacing: ${t.typography.subtitle.letterSpacing};
  text-transform: ${t.typography.subtitle.textTransform};
  overflow-wrap: anywhere;
}
.product-feature-subtitle strong,
.product-feature-subtitle b {
  color: var(--product-feature-accent);
}
.product-feature-description {
  max-width: 560px;
  margin: 0;
  color: var(--product-feature-text-soft);
  font-size: ${t.typography.description.fontSize}px;
  line-height: ${t.typography.description.lineHeight};
  font-weight: ${t.typography.description.fontWeight};
  letter-spacing: ${t.typography.description.letterSpacing};
  overflow-wrap: anywhere;
}
.product-feature-features {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 0;
  max-width: 620px;
  margin-top: 6px;
}
.product-feature-feature {
  --product-feature-point-accent: var(--product-feature-accent);
  display: grid;
  min-width: 0;
  justify-items: center;
  gap: 10px;
  padding: 0 18px;
  text-align: center;
}
.product-feature-feature + .product-feature-feature {
  border-left: 1px solid var(--product-feature-divider);
}
.product-feature-feature-icon {
  display: inline-flex;
  width: 54px;
  height: 54px;
  align-items: center;
  justify-content: center;
  border: 1px solid color-mix(in srgb, var(--product-feature-point-accent) 42%, var(--product-feature-border));
  border-radius: ${t.radius.icon}px;
  background: color-mix(in srgb, var(--product-feature-point-accent) 10%, transparent);
  color: var(--product-feature-point-accent);
}
.product-feature-feature-icon .product-feature-icon,
.product-feature-feature-icon .product-feature-icon svg {
  width: 25px;
  height: 25px;
}
.product-feature-feature-label {
  display: block;
  max-width: 100%;
  color: var(--product-feature-text);
  font-size: ${t.typography.feature.fontSize}px;
  line-height: ${t.typography.feature.lineHeight};
  font-weight: ${t.typography.feature.fontWeight};
  letter-spacing: ${t.typography.feature.letterSpacing};
  overflow-wrap: anywhere;
}
.product-feature-feature-description {
  display: block;
  max-width: 100%;
  margin-top: 3px;
  color: var(--product-feature-text-muted);
  font-size: 12px;
  line-height: 1.35;
  font-weight: 500;
  letter-spacing: 0;
  overflow-wrap: anywhere;
}
.product-feature-actions {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: ${t.spacing.actionGap}px;
  margin-top: 2px;
}
.product-feature-action {
  display: inline-flex;
  max-width: 100%;
  min-height: 58px;
  align-items: center;
  justify-content: center;
  gap: 13px;
  border-radius: ${t.radius.button}px;
  text-decoration: none;
  transition: transform 180ms ease, background 180ms ease, border-color 180ms ease;
}
.product-feature-action:hover {
  transform: translateY(-1px);
}
.product-feature-action:focus-visible,
.product-feature-image-link:focus-visible,
.product-feature-image-hotspot:focus-visible {
  outline: 3px solid var(--product-feature-focus);
  outline-offset: 4px;
}
.product-feature-action-primary {
  padding: 0 28px;
  border: 1px solid var(--product-feature-accent);
  background: linear-gradient(180deg, var(--product-feature-accent), var(--product-feature-accent-dark));
  color: var(--product-feature-button-text);
  box-shadow: 0 18px 34px rgba(0, 0, 0, 0.24);
  font-size: ${t.typography.cta.fontSize}px;
  line-height: ${t.typography.cta.lineHeight};
  font-weight: ${t.typography.cta.fontWeight};
  letter-spacing: ${t.typography.cta.letterSpacing};
  text-transform: ${t.typography.cta.textTransform};
}
.product-feature-action-secondary {
  min-height: 48px;
  padding: 0 2px;
  border-bottom: 1px solid var(--product-feature-text-soft);
  border-radius: 0;
  color: var(--product-feature-text);
  font-size: ${t.typography.secondaryCta.fontSize}px;
  line-height: ${t.typography.secondaryCta.lineHeight};
  font-weight: ${t.typography.secondaryCta.fontWeight};
  letter-spacing: ${t.typography.secondaryCta.letterSpacing};
}
.product-feature-action-secondary:hover {
  border-bottom-color: var(--product-feature-accent);
  color: var(--product-feature-accent);
}
.product-feature-action-label {
  min-width: 0;
  overflow-wrap: anywhere;
}
.product-feature-action .product-feature-icon,
.product-feature-action .product-feature-icon svg {
  display: block;
  flex: 0 0 auto;
  width: 23px;
  height: 23px;
}
.product-feature-footnote {
  display: inline-flex;
  max-width: 100%;
  align-items: center;
  gap: 8px;
  margin: 0;
  color: var(--product-feature-text-muted);
  font-size: ${t.typography.footnote.fontSize}px;
  line-height: ${t.typography.footnote.lineHeight};
  font-weight: ${t.typography.footnote.fontWeight};
  letter-spacing: ${t.typography.footnote.letterSpacing};
  overflow-wrap: anywhere;
}
.product-feature-footnote .product-feature-icon,
.product-feature-footnote .product-feature-icon svg {
  width: 15px;
  height: 15px;
  flex: 0 0 auto;
}
.product-feature-media {
  position: relative;
  display: flex;
  min-width: 0;
  min-height: var(--product-feature-media-min-height);
  align-items: center;
  justify-content: center;
  padding: 10px;
}
.product-feature-image-link {
  display: block;
  width: min(100%, var(--product-feature-product-max-width));
  color: inherit;
  text-decoration: none;
}
.product-feature-product {
  position: relative;
  width: 100%;
  border-radius: ${t.radius.media}px;
}
.product-feature-product-image {
  display: block;
  width: 100%;
  max-height: calc(var(--product-feature-min-height) - 90px);
  object-fit: contain;
  object-position: var(--product-feature-product-position);
  filter: drop-shadow(var(--product-feature-product-shadow));
}
.product-feature-product-fallback {
  display: flex;
  width: 100%;
  min-height: 340px;
  align-items: center;
  justify-content: center;
  padding: 22px;
  border: 1px solid var(--product-feature-border);
  border-radius: ${t.radius.media}px;
  background: var(--product-feature-surface-soft);
  color: var(--product-feature-text-muted);
  font-size: 15px;
  line-height: 1.4;
  font-weight: 800;
  text-align: center;
}
.product-feature-image-hotspot {
  position: absolute;
  inset: 0;
  z-index: 2;
  display: block;
  color: inherit;
  text-decoration: none;
}
.product-feature-sr-only {
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  white-space: nowrap;
  border: 0;
}
@container (max-width: 1080px) {
  .product-feature-shell {
    padding: ${t.spacing.tabletShellY}px ${t.spacing.tabletShellX}px;
  }
  .product-feature-layout {
    min-height: ${t.sizes.tabletMinHeight}px;
    grid-template-columns: minmax(0, 0.95fr) minmax(260px, 0.8fr);
    gap: 24px;
    padding: ${t.spacing.tabletFramePaddingY}px ${t.spacing.tabletFramePaddingX}px;
  }
  .product-feature-title {
    font-size: ${t.typography.title.tabletFontSize}px;
  }
  .product-feature-subtitle {
    font-size: ${t.typography.subtitle.tabletFontSize}px;
  }
  .product-feature-description {
    font-size: ${t.typography.description.tabletFontSize}px;
  }
  .product-feature-media {
    min-height: ${t.sizes.tabletMediaMinHeight}px;
  }
  .product-feature-image-link {
    width: min(100%, ${t.sizes.tabletProductMaxWidth}px);
  }
}
@container (max-width: 760px) {
  .product-feature-shell {
    padding: ${t.spacing.mobileShellY}px ${t.spacing.mobileShellX}px;
  }
  .product-feature-frame {
    min-height: var(--product-feature-mobile-min-height);
  }
  .product-feature-background-image {
    object-position: var(--product-feature-bg-mobile-position);
  }
  .product-feature-scrim {
    background:
      linear-gradient(180deg, var(--product-feature-overlay-start) 0%, var(--product-feature-overlay-middle) 50%, var(--product-feature-overlay-end) 100%),
      linear-gradient(90deg, rgba(0, 0, 0, 0.28) 0%, rgba(0, 0, 0, 0.12) 100%);
  }
  .product-feature-layout,
  .product-feature-frame[data-has-media="false"] .product-feature-layout {
    min-height: var(--product-feature-mobile-min-height);
    grid-template-columns: minmax(0, 1fr);
    align-content: start;
    gap: 22px;
    padding: ${t.spacing.mobileFramePaddingY}px ${t.spacing.mobileFramePaddingX}px;
  }
  .product-feature-frame[data-media-placement="left"] .product-feature-copy,
  .product-feature-frame[data-media-placement="left"] .product-feature-media {
    order: initial;
  }
  .product-feature-copy {
    max-width: 100%;
    gap: 16px;
  }
  .product-feature-eyebrow {
    padding-bottom: 7px;
    font-size: ${t.typography.eyebrow.mobileFontSize}px;
  }
  .product-feature-title {
    font-size: ${t.typography.title.mobileFontSize}px;
    line-height: ${t.typography.title.mobileLineHeight};
  }
  .product-feature-subtitle {
    font-size: ${t.typography.subtitle.mobileFontSize}px;
  }
  .product-feature-description {
    font-size: ${t.typography.description.mobileFontSize}px;
    line-height: 1.5;
  }
  .product-feature-features {
    grid-template-columns: repeat(auto-fit, minmax(92px, 1fr));
    margin-top: 2px;
  }
  .product-feature-feature {
    gap: 8px;
    padding: 0 8px;
  }
  .product-feature-feature-icon {
    width: 42px;
    height: 42px;
  }
  .product-feature-feature-icon .product-feature-icon,
  .product-feature-feature-icon .product-feature-icon svg {
    width: 20px;
    height: 20px;
  }
  .product-feature-feature-label {
    font-size: ${t.typography.feature.mobileFontSize}px;
  }
  .product-feature-actions {
    gap: 12px;
  }
  .product-feature-action-primary {
    width: 100%;
    min-height: 54px;
    padding: 0 18px;
    font-size: ${t.typography.cta.mobileFontSize}px;
  }
  .product-feature-action-secondary {
    min-height: 40px;
    font-size: ${t.typography.secondaryCta.mobileFontSize}px;
  }
  .product-feature-footnote {
    font-size: ${t.typography.footnote.mobileFontSize}px;
  }
  .product-feature-media {
    min-height: var(--product-feature-mobile-media-min-height, ${t.sizes.mobileMediaMinHeight}px);
    padding: 0;
  }
  .product-feature-image-link {
    width: min(100%, ${t.sizes.mobileProductMaxWidth}px);
  }
  .product-feature-product-image {
    max-height: 360px;
  }
}
@container (max-width: 390px) {
  .product-feature-title {
    font-size: 34px;
  }
  .product-feature-feature {
    padding: 0 6px;
  }
  .product-feature-action-primary {
    gap: 10px;
    font-size: 14px;
  }
}
`;

function getCssSize(value: number | string): string {
  return typeof value === "number" ? `${value}px` : value;
}

function getImageSrc(image?: ProductFeatureImage): string {
  if (!image) return "";
  return image.thumbnail || image.src || image.url || image.original || "";
}

function getImageAlt(image: ProductFeatureImage | undefined, fallback?: React.ReactNode): string {
  if (image?.alt || image?.title) return image.alt || image.title || "";
  return typeof fallback === "string" ? fallback : "";
}

function getColors({
  colors,
  colours,
  accentColor,
  accentColour,
  accentDarkColor,
  accentDarkColour,
  accentSoftColor,
  accentSoftColour,
  backgroundColor,
  backgroundColour,
  surfaceColor,
  surfaceColour,
  textColor,
  textColour,
  mutedTextColor,
  mutedTextColour,
}: Pick<
  ProductFeatureProps,
  | "colors"
  | "colours"
  | "accentColor"
  | "accentColour"
  | "accentDarkColor"
  | "accentDarkColour"
  | "accentSoftColor"
  | "accentSoftColour"
  | "backgroundColor"
  | "backgroundColour"
  | "surfaceColor"
  | "surfaceColour"
  | "textColor"
  | "textColour"
  | "mutedTextColor"
  | "mutedTextColour"
>) {
  const resolved = {
    ...t.colors,
    ...colours,
    ...colors,
  };

  return {
    ...resolved,
    accent: accentColor || accentColour || resolved.accent,
    accentDark: accentDarkColor || accentDarkColour || resolved.accentDark,
    accentSoft: accentSoftColor || accentSoftColour || resolved.accentSoft,
    background: backgroundColor || backgroundColour || resolved.background,
    surface: surfaceColor || surfaceColour || resolved.surface,
    text: textColor || textColour || resolved.text,
    textMuted: mutedTextColor || mutedTextColour || resolved.textMuted,
  };
}

function getLinkAttributes({
  openInNewTab,
  target,
  rel,
}: {
  openInNewTab?: boolean;
  target?: string;
  rel?: string;
}) {
  const resolvedTarget = target || (openInNewTab ? "_blank" : undefined);
  const resolvedRel =
    rel || (resolvedTarget === "_blank" ? "noopener noreferrer" : undefined);

  return {
    target: resolvedTarget,
    rel: resolvedRel,
  };
}

function resolveIconToken(token?: ProductFeatureIconToken): ProductFeatureIconToken | undefined {
  if (!token || !(token in iconRegistry)) return undefined;
  return token;
}

function ProductFeatureIcon({
  icon,
  token,
  fallbackToken,
  size = 20,
}: {
  icon?: React.ReactNode;
  token?: ProductFeatureIconToken;
  fallbackToken?: ProductFeatureIconToken;
  size?: number;
}) {
  if (icon) {
    return (
      <span
        className="product-feature-icon"
        style={{ width: size, height: size }}
        aria-hidden="true"
      >
        {icon}
      </span>
    );
  }

  const resolvedToken = resolveIconToken(token) || resolveIconToken(fallbackToken);
  const Icon = resolvedToken ? iconRegistry[resolvedToken] : undefined;

  if (!Icon) return null;

  return (
    <Icon
      className="product-feature-icon"
      size={size}
      strokeWidth={2.3}
      aria-hidden="true"
    />
  );
}

function renderAction({
  action,
  className,
  fallbackIconToken,
  defaultIconPosition,
  Link,
  style,
}: {
  action?: ProductFeatureAction;
  className: string;
  fallbackIconToken: ProductFeatureIconToken;
  defaultIconPosition: "start" | "end";
  Link: ProductFeatureLinkComponent;
  style?: CSSProperties;
}) {
  if (!action?.href || !action.label) return null;

  const iconPosition = action.iconPosition || defaultIconPosition;
  const linkAttributes = getLinkAttributes({
    openInNewTab: action.openInNewTab,
    target: action.target,
    rel: action.rel,
  });
  const icon = action.hideIcon ? null : (
    <ProductFeatureIcon
      icon={action.icon}
      token={action.iconToken}
      fallbackToken={fallbackIconToken}
      size={22}
    />
  );

  return (
    <Link
      href={action.href}
      className={className}
      aria-label={action.ariaLabel}
      title={action.title}
      style={style}
      {...linkAttributes}
    >
      {iconPosition === "start" ? icon : null}
      <span className="product-feature-action-label">{action.label}</span>
      {iconPosition === "end" ? icon : null}
    </Link>
  );
}

function getFootnote(footnote?: React.ReactNode | ProductFeatureNote): ProductFeatureNote | undefined {
  if (!footnote) return undefined;

  if (
    typeof footnote === "object" &&
    !React.isValidElement(footnote) &&
    "label" in footnote
  ) {
    return footnote as ProductFeatureNote;
  }

  return {
    label: footnote,
    iconToken: t.icons.footnote as ProductFeatureIconToken,
  };
}

export default function ProductFeature({
  eyebrow,
  title,
  titleComponent = "h2",
  subtitle,
  description,
  features = [],
  primaryAction,
  secondaryAction,
  footnote,
  backgroundImage,
  backgroundImageSrc,
  backgroundImageAlt,
  backgroundObjectPosition,
  backgroundMobileObjectPosition,
  backgroundOpacity = 1,
  backgroundFilter = "none",
  productImage,
  productImageSrc,
  productImageAlt,
  productObjectPosition,
  imageHref,
  imageAriaLabel,
  imageOpenInNewTab,
  imageTarget,
  imageRel,
  mediaPlacement = "right",
  Link = DefaultLink,
  colors,
  colours,
  accentColor,
  accentColour,
  accentDarkColor,
  accentDarkColour,
  accentSoftColor,
  accentSoftColour,
  backgroundColor,
  backgroundColour,
  surfaceColor,
  surfaceColour,
  textColor,
  textColour,
  mutedTextColor,
  mutedTextColour,
  maxWidth = t.sizes.maxWidth,
  minHeight = t.sizes.minHeight,
  mobileMinHeight = t.sizes.mobileMinHeight,
  mediaMinHeight = t.sizes.mediaMinHeight,
  productMaxWidth = t.sizes.productMaxWidth,
  className = "",
  style,
  styles,
  ariaLabel,
}: ProductFeatureProps) {
  const background = backgroundImage || (backgroundImageSrc ? {
    src: backgroundImageSrc,
    alt: backgroundImageAlt,
  } : undefined);
  const product = productImage || (productImageSrc ? {
    src: productImageSrc,
    alt: productImageAlt,
  } : undefined);
  const backgroundSrc = getImageSrc(background);
  const productSrc = getImageSrc(product);
  const resolvedColors = getColors({
    colors,
    colours,
    accentColor,
    accentColour,
    accentDarkColor,
    accentDarkColour,
    accentSoftColor,
    accentSoftColour,
    backgroundColor,
    backgroundColour,
    surfaceColor,
    surfaceColour,
    textColor,
    textColour,
    mutedTextColor,
    mutedTextColour,
  });
  const resolvedImageHref = imageHref || product?.href || background?.href;
  const resolvedImageOpenInNewTab =
    imageOpenInNewTab ??
    product?.openInNewTab ??
    background?.openInNewTab ??
    t.defaults.imageOpenInNewTab;
  const imageLinkAttributes = getLinkAttributes({
    openInNewTab: resolvedImageOpenInNewTab,
    target: imageTarget || product?.target || background?.target,
    rel: imageRel || product?.rel || background?.rel,
  });
  const imageLabel =
    imageAriaLabel ||
    product?.ariaLabel ||
    background?.ariaLabel ||
    (typeof title === "string" ? `Open ${title}` : "Open product image");
  const hasMedia = Boolean(backgroundSrc || productSrc || product?.title || resolvedImageHref);
  const TitleTag = titleComponent;
  const note = getFootnote(footnote);

  return (
    <section
      className={`product-feature ${className}`.trim()}
      aria-label={ariaLabel || (typeof title === "string" ? title : "Featured product")}
      style={
        {
          "--product-feature-bg": resolvedColors.background,
          "--product-feature-surface": resolvedColors.surface,
          "--product-feature-surface-soft": resolvedColors.surfaceSoft,
          "--product-feature-text": resolvedColors.text,
          "--product-feature-text-soft": resolvedColors.textSoft,
          "--product-feature-text-muted": resolvedColors.textMuted,
          "--product-feature-accent": resolvedColors.accent,
          "--product-feature-accent-dark": resolvedColors.accentDark,
          "--product-feature-accent-soft": resolvedColors.accentSoft,
          "--product-feature-border": resolvedColors.border,
          "--product-feature-divider": resolvedColors.divider,
          "--product-feature-button-text": resolvedColors.buttonText,
          "--product-feature-image-fallback": resolvedColors.imageFallback,
          "--product-feature-focus": resolvedColors.focus,
          "--product-feature-overlay-start": resolvedColors.overlayStart,
          "--product-feature-overlay-middle": resolvedColors.overlayMiddle,
          "--product-feature-overlay-end": resolvedColors.overlayEnd,
          "--product-feature-shadow": resolvedColors.shadow,
          "--product-feature-product-shadow": resolvedColors.productShadow,
          "--product-feature-max-width": getCssSize(maxWidth),
          "--product-feature-min-height": getCssSize(minHeight),
          "--product-feature-mobile-min-height": getCssSize(mobileMinHeight),
          "--product-feature-media-min-height": getCssSize(mediaMinHeight),
          "--product-feature-product-max-width": getCssSize(productMaxWidth),
          "--product-feature-bg-position":
            backgroundObjectPosition ||
            background?.objectPosition ||
            t.defaults.backgroundObjectPosition,
          "--product-feature-bg-mobile-position":
            backgroundMobileObjectPosition ||
            background?.mobileObjectPosition ||
            background?.objectPosition ||
            t.defaults.backgroundMobileObjectPosition,
          "--product-feature-product-position":
            productObjectPosition ||
            product?.objectPosition ||
            t.defaults.productObjectPosition,
          "--product-feature-bg-opacity": backgroundOpacity,
          "--product-feature-bg-filter": backgroundFilter,
          ...styles?.root,
          ...style,
        } as ProductFeatureStyle
      }
    >
      <style>{productFeatureCss}</style>

      <div className="product-feature-shell" style={styles?.shell}>
        <article
          className="product-feature-frame"
          data-has-media={hasMedia ? "true" : "false"}
          data-media-placement={mediaPlacement}
          style={styles?.frame}
        >
          {backgroundSrc ? (
            <div className="product-feature-background" aria-hidden="true">
              <img
                src={backgroundSrc}
                alt=""
                className="product-feature-background-image"
                loading={background?.loading || "eager"}
              />
            </div>
          ) : null}

          <div className="product-feature-scrim" aria-hidden="true" />

          <div className="product-feature-layout" style={styles?.layout}>
            <div className="product-feature-copy" style={styles?.copy}>
              {eyebrow ? (
                <p className="product-feature-eyebrow" style={styles?.eyebrow}>
                  {eyebrow}
                </p>
              ) : null}

              {title ? (
                <TitleTag className="product-feature-title" style={styles?.title}>
                  {title}
                </TitleTag>
              ) : null}

              {subtitle ? (
                <p className="product-feature-subtitle" style={styles?.subtitle}>
                  {subtitle}
                </p>
              ) : null}

              {description ? (
                <p className="product-feature-description" style={styles?.description}>
                  {description}
                </p>
              ) : null}

              {features.length ? (
                <div className="product-feature-features" style={styles?.features}>
                  {features.map((feature, index) => (
                    <div
                      key={feature.id ?? index}
                      className="product-feature-feature"
                      style={
                        {
                          "--product-feature-point-accent":
                            feature.accentColor || resolvedColors.accent,
                          ...styles?.feature,
                        } as ProductFeatureStyle
                      }
                    >
                      <span className="product-feature-feature-icon">
                        <ProductFeatureIcon
                          icon={feature.icon}
                          token={feature.iconToken}
                          fallbackToken={
                            index === 0
                              ? (t.icons.featureOne as ProductFeatureIconToken)
                              : index === 1
                                ? (t.icons.featureTwo as ProductFeatureIconToken)
                                : (t.icons.featureThree as ProductFeatureIconToken)
                          }
                          size={24}
                        />
                      </span>
                      <span>
                        <span className="product-feature-feature-label">
                          {feature.label}
                        </span>
                        {feature.description ? (
                          <span className="product-feature-feature-description">
                            {feature.description}
                          </span>
                        ) : null}
                      </span>
                    </div>
                  ))}
                </div>
              ) : null}

              {primaryAction || secondaryAction ? (
                <div className="product-feature-actions" style={styles?.actions}>
                  {renderAction({
                    action: primaryAction,
                    className: "product-feature-action product-feature-action-primary",
                    fallbackIconToken: t.icons.primaryAction as ProductFeatureIconToken,
                    defaultIconPosition: "start",
                    Link,
                    style: styles?.primaryAction,
                  })}
                  {renderAction({
                    action: secondaryAction,
                    className: "product-feature-action product-feature-action-secondary",
                    fallbackIconToken: t.icons.secondaryAction as ProductFeatureIconToken,
                    defaultIconPosition: "end",
                    Link,
                    style: styles?.secondaryAction,
                  })}
                </div>
              ) : null}

              {note ? (
                <p className="product-feature-footnote" style={styles?.footnote}>
                  <ProductFeatureIcon
                    icon={note.icon}
                    token={note.iconToken}
                    fallbackToken={t.icons.footnote as ProductFeatureIconToken}
                    size={15}
                  />
                  <span>{note.label}</span>
                </p>
              ) : null}
            </div>

            {hasMedia ? (
              <div className="product-feature-media" style={styles?.media}>
                {productSrc || product?.title ? (
                  resolvedImageHref ? (
                    <Link
                      href={resolvedImageHref}
                      className="product-feature-image-link"
                      aria-label={imageLabel}
                      title={product?.title}
                      {...imageLinkAttributes}
                    >
                      <div className="product-feature-product">
                        {productSrc ? (
                          <img
                            src={productSrc}
                            alt={getImageAlt(product, title)}
                            className="product-feature-product-image"
                            loading={product?.loading || "eager"}
                            style={styles?.productImage}
                          />
                        ) : (
                          <span className="product-feature-product-fallback">
                            {product?.title || title}
                          </span>
                        )}
                      </div>
                    </Link>
                  ) : (
                    <div className="product-feature-image-link">
                      <div className="product-feature-product">
                        {productSrc ? (
                          <img
                            src={productSrc}
                            alt={getImageAlt(product, title)}
                            className="product-feature-product-image"
                            loading={product?.loading || "eager"}
                            style={styles?.productImage}
                          />
                        ) : (
                          <span className="product-feature-product-fallback">
                            {product?.title || title}
                          </span>
                        )}
                      </div>
                    </div>
                  )
                ) : null}

                {!productSrc && !product?.title && resolvedImageHref ? (
                  <Link
                    href={resolvedImageHref}
                    className="product-feature-image-hotspot"
                    aria-label={imageLabel}
                    {...imageLinkAttributes}
                  >
                    <span className="product-feature-sr-only">{imageLabel}</span>
                  </Link>
                ) : null}
              </div>
            ) : null}
          </div>
        </article>
      </div>
    </section>
  );
}
